/** The Sketch's tool rail (floating on the map) with each tool's flyout of variants, and the stage bar docked above
 * the map (the active tool, its hint, the footprint steps, and Magnetic | Free · Portrait | Landscape for panels).
 * Presentational: state lives in SketchEditor; the variants come from variants.ts. Styling: sketch.css (`.sk-tools…`,
 * `.sk-flyout…`, `.sk-stagebar…`). The tools' options are the side panel's first section (SketchToolPanel.tsx). */
import { AppWindow, Box, BrickWall, Building2, ChevronDown, ChevronRight, CircleDot, Cylinder, Hand, Magnet, MapPin, MousePointer2, Move3d, PenLine, RectangleHorizontal, RectangleVertical, SquareDashed, SunMedium, TreeDeciduous, Trees, TreePine } from "lucide-react";
import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { Orientation } from "../editor/snap.ts";
import type { DormerKind } from "./dormers.ts";
import type { PlaceMode } from "./gizmo.ts";
import type { EdgeType } from "./skeleton.ts";
import type { SketchTool } from "./types.ts";
import type { MessageKey } from "../i18n/keys";
import { DirIcon } from "../i18n/mirror";
import { structureLabel } from "./structures.ts";
import { useSketchText } from "./useText.ts";
import { variantsOf, type ToolState } from "./variants.ts";

export type { ShadeOptions } from "./variants.ts";

export const TOOL_KEYS: Record<SketchTool, string> = { select: "V", line: "L", footprint: "R", panel: "P", pan: "H", tree: "T", obstacle: "C", neighbour: "N", dormer: "D", note: "I" };

export const TOOL_LABELS: Record<SketchTool, MessageKey> = {
  select: "sketch.tool.select",
  pan: "sketch.tool.pan",
  footprint: "sketch.tool.footprint",
  line: "sketch.tool.line",
  dormer: "sketch.tool.dormer",
  panel: "sketch.tool.panel",
  tree: "sketch.tool.tree",
  obstacle: "sketch.tool.obstacle",
  neighbour: "sketch.tool.neighbour",
  note: "sketch.tool.note",
};

export function ToolIcon({ tool, size = 19 }: { tool: SketchTool; size?: number }) {
  switch (tool) {
    case "select": return <MousePointer2 size={size} />;
    case "pan": return <Hand size={size} />;
    case "footprint": return <SquareDashed size={size} />;
    case "line": return <PenLine size={size} />;
    case "dormer": return <DormerIcon size={size} />;
    case "panel": return <SunMedium size={size} />;
    case "tree": return <TreeDeciduous size={size} />;
    case "obstacle": return <Box size={size} />;
    case "neighbour": return <Building2 size={size} />;
    case "note": return <MapPin size={size} />;
  }
}

/** The rail, in groups: navigate · draw the roof · place modules · what shades the roof · notes. */
const TOOLS: SketchTool[][] = [["select", "pan"], ["footprint", "line", "dormer"], ["panel"], ["tree", "obstacle", "neighbour"], ["note"]];

export const LINE_TYPES: [EdgeType, MessageKey][] = [
  ["eave", "sketch.line.eave"],
  ["ridge", "sketch.line.ridge"],
  ["hip", "sketch.line.hip"],
  ["valley", "sketch.line.valley"],
  ["rake", "sketch.line.rake"],
];

/** A dormer: a small gable roof standing out of a slope. */
function DormerIcon({ size = 19 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 20 L12 6 L22 20" />
      <path d="M8.5 15.5 V11 L12 8 L15.5 11 V15.5" />
      <path d="M10.5 15.5 V13 H13.5 V15.5" />
    </svg>
  );
}

/** A variant's glyph in the flyouts and the tool section. */
export function VariantGlyph({ tool, id }: { tool: SketchTool; id: string }) {
  if (tool === "obstacle") return id === "vent" ? <CircleDot size={16} /> : id === "skylight" ? <AppWindow size={16} /> : id === "cylinder" ? <Cylinder size={16} /> : id === "chimney" ? <ChimneyGlyph /> : <Box size={16} />;
  if (tool === "tree") return id === "conifer" ? <TreePine size={16} /> : id === "evergreen" ? <Trees size={16} /> : <TreeDeciduous size={16} />;
  if (tool === "panel") return id.endsWith("landscape") ? <RectangleHorizontal size={16} /> : <RectangleVertical size={16} />;
  if (tool === "dormer") return <DormerGlyph kind={id as DormerKind} />;
  if (tool === "line") return <i className={`sk-swatch sk-swatch-${id}`} aria-hidden />;
  if (tool === "neighbour") return id === "wall" ? <BrickWall size={16} /> : <Building2 size={16} />;
  return null;
}

function ChimneyGlyph() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" aria-hidden>
      <path d="M3 21 L12 12 L21 21" />
      <path d="M14.5 14.5 V6 H18.5 V18.5" />
      <path d="M13.5 6 H19.5" />
    </svg>
  );
}

const HOVER_MS = 380;
const LONG_PRESS_MS = 420;

/** The rail. Hovering a tool that has variants (or a long press, or → on the keyboard) opens its flyout to the right;
 * picking a variant selects the tool with it. A small corner mark says which tools have one. */
export function SketchToolbar({ tool, onTool: pick, readOnly, state, onVariant: pickVariant, collapsible = false }: { tool: SketchTool; onTool: (t: SketchTool) => void; readOnly: boolean; state: Omit<ToolState, "tool">; onVariant: (tool: SketchTool, id: string) => void; collapsible?: boolean }) {
  const tx = useSketchText();
  const [open, setOpen] = useState<SketchTool | null>(null);
  // a phone (layout.ts): the rail is a bar at the bottom of the map, folded to the active tool until opened; picking a
  // tool folds it again so the map stays free
  const [expanded, setExpanded] = useState(false);
  const folded = collapsible && !expanded;
  const onTool = (t: SketchTool) => {
    pick(t);
    if (collapsible) setExpanded(false);
  };
  const onVariant = (t: SketchTool, id: string) => {
    pickVariant(t, id);
    if (collapsible) setExpanded(false);
  };
  const timer = useRef<number | null>(null);
  const press = useRef<{ t: number; tool: SketchTool; opened: boolean } | null>(null);
  const clear = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => clear, []);
  useEffect(() => {
    if (!open) return;
    const key = (e: KeyboardEvent) => e.key === "Escape" && setOpen(null);
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [open]);
  const later = (fn: () => void, ms: number) => {
    clear();
    timer.current = window.setTimeout(fn, ms);
  };
  return (
    <nav className={`sk-tools sk-glass${collapsible ? " is-bar" : ""}${folded ? " is-folded" : ""}`} role="toolbar" aria-label={tx.t("sketch.tools.label")} aria-orientation={collapsible ? "horizontal" : "vertical"} onPointerLeave={() => later(() => setOpen(null), 220)}>
      {collapsible && (
        <button type="button" className="sk-tools-toggle" aria-expanded={expanded} aria-label={expanded ? tx.t("sketch.tools.fold") : tx.t("sketch.tools.unfold", { tool: tx.t(TOOL_LABELS[tool]) })} onClick={() => setExpanded((x) => !x)}>
          {expanded ? <ChevronDown size={18} /> : <ToolIcon tool={tool} />}
          {!expanded && <span>{tx.t("sketch.tools.short")}</span>}
        </button>
      )}
      {!folded && TOOLS.map((group, g) => (
        <Fragment key={g}>
          {g > 0 && <span className="sk-tools-sep" aria-hidden />}
          {group.map((id) => {
            const variants = variantsOf(id, state);
            const disabled = readOnly && id !== "select" && id !== "pan";
            const has = variants.length > 0 && !disabled;
            return (
              <div key={id} className={`sk-tool-wrap${open === id ? " is-open" : ""}`} onPointerEnter={() => (has ? later(() => setOpen(id), open ? 60 : HOVER_MS) : later(() => setOpen(null), 120))}>
                <button
                  type="button"
                  className={`sk-tool${tool === id ? " is-active" : ""}${has ? " has-variants" : ""}`}
                  onClick={() => {
                    if (press.current?.opened) return void (press.current = null);
                    onTool(id);
                  }}
                  onPointerDown={(e) => {
                    if (!has || e.pointerType === "mouse") return;
                    press.current = { t: Date.now(), tool: id, opened: false };
                    later(() => {
                      if (press.current) press.current.opened = true;
                      setOpen(id);
                    }, LONG_PRESS_MS);
                  }}
                  onPointerUp={() => press.current && !press.current.opened && clear()}
                  onKeyDown={(e) => {
                    if (has && e.key === (document.documentElement.dir === "rtl" ? "ArrowLeft" : "ArrowRight")) {
                      e.preventDefault();
                      setOpen(id);
                    }
                  }}
                  disabled={disabled}
                  aria-label={tx.t("sketch.tools.withKey", { tool: tx.t(TOOL_LABELS[id]), key: TOOL_KEYS[id] })}
                  aria-pressed={tool === id}
                  aria-keyshortcuts={TOOL_KEYS[id]}
                  aria-haspopup={has ? "menu" : undefined}
                  aria-expanded={has ? open === id : undefined}
                >
                  <ToolIcon tool={id} />
                  {has && <i className="sk-tool-corner" aria-hidden />}
                  {open !== id && (
                    <span className="sk-tip" aria-hidden>
                      {tx.t(TOOL_LABELS[id])} <kbd className="sk-kbd">{TOOL_KEYS[id]}</kbd>
                    </span>
                  )}
                </button>
                {open === id && (
                  <div className="sk-flyout" role="menu" aria-label={tx.t(TOOL_LABELS[id])} onPointerEnter={clear}>
                    <div className="sk-flyout-head">
                      <b>{tx.t(TOOL_LABELS[id])}</b>
                      <kbd className="sk-kbd">{TOOL_KEYS[id]}</kbd>
                    </div>
                    {variants.map((v, i) => (
                      <button
                        key={v.id}
                        type="button"
                        role="menuitemradio"
                        aria-checked={v.active && tool === id}
                        className={`sk-flyout-item${v.active ? " is-current" : ""}${v.active && tool === id ? " is-active" : ""}`}
                        autoFocus={i === 0 && document.activeElement?.closest?.(".sk-tools") != null}
                        onClick={() => {
                          onVariant(id, v.id);
                          setOpen(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === (document.documentElement.dir === "rtl" ? "ArrowRight" : "ArrowLeft") || e.key === "Escape") {
                            e.preventDefault();
                            setOpen(null);
                          }
                          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                            e.preventDefault();
                            const items = [...(e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(".sk-flyout-item") ?? [])];
                            const at = items.indexOf(e.currentTarget);
                            items[(at + (e.key === "ArrowDown" ? 1 : items.length - 1)) % items.length]?.focus();
                          }
                        }}
                      >
                        <span className="sk-flyout-glyph"><VariantGlyph tool={id} id={v.id} /></span>
                        {tx.t(v.label)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </Fragment>
      ))}
    </nav>
  );
}

/** The bar docked above the map: the active tool and what a click does; footprint steps; and, for panels, the
 * placement mode and the orientation as two segmented controls whose keys show on hover. */
export function StageBar(props: {
  tool: SketchTool;
  placeMode: PlaceMode;
  onPlaceMode: (m: PlaceMode) => void;
  orientation: Orientation;
  onOrientation: (o: Orientation) => void;
  /** Footprint: the corners clicked so far, and the building it draws. */
  corners?: number;
  structureName?: string | null;
  hint: ReactNode;
  readOnly: boolean;
}) {
  const tx = useSketchText();
  const panels = props.tool === "panel" || props.tool === "select";
  const n = props.corners ?? 0;
  const steps: [string, string][] = [
    [tx.t("sketch.stage.step.corners"), n ? tx.t("sketch.stage.step.cornersCount", { count: n }) : props.structureName && props.structureName !== "House" ? structureLabel(tx, props.structureName) : ""],
    [tx.t("sketch.stage.step.close"), tx.t("sketch.stage.step.closeDetail")],
    [tx.t("sketch.stage.step.pickShape"), ""],
  ];
  return (
    <div className="sk-stagebar" role="region" aria-label={tx.t("sketch.stage.label")}>
      <span className="sk-stagebar-tool">
        <span className="sk-stagebar-icon"><ToolIcon tool={props.tool} size={16} /></span>
        <b>{tx.t(TOOL_LABELS[props.tool])}</b>
        <kbd className="sk-kbd">{TOOL_KEYS[props.tool]}</kbd>
      </span>
      {props.tool === "footprint" ? (
        <ol className="sk-steps" aria-label={tx.t("sketch.tool.footprint")}>
          {steps.map(([title, detail], i) => {
            const step = n < 3 ? 0 : 1;
            return (
              <Fragment key={title}>
                {i > 0 && <DirIcon><ChevronRight className="sk-step-arrow" size={14} aria-hidden /></DirIcon>}
                <li className={`sk-step${i === step ? " is-current" : i < step ? " is-done" : ""}`} aria-current={i === step ? "step" : undefined}>
                  <i>{i + 1}</i>
                  <span>
                    {title}
                    {detail && <span className="sk-step-text-long"> · {detail}</span>}
                  </span>
                </li>
              </Fragment>
            );
          })}
        </ol>
      ) : (
        <span className="sk-stagebar-hint">{props.hint}</span>
      )}
      {panels && (
        <span className="sk-stagebar-modes">
          <PlaceModeSwitch value={props.placeMode} onChange={props.onPlaceMode} disabled={props.readOnly} />
          <OrientationSwitch value={props.orientation} onChange={props.onOrientation} disabled={props.readOnly} />
        </span>
      )}
    </div>
  );
}

/** Magnetic | Free, with its key (M) on hover. */
export function PlaceModeSwitch({ value, onChange, disabled }: { value: PlaceMode; onChange: (m: PlaceMode) => void; disabled?: boolean }) {
  const tx = useSketchText();
  return (
    <div className={`sk-segmented sk-keyed${value === "magnetic" ? " is-magnetic" : ""}`} role="radiogroup" aria-label={tx.t("sketch.placement.label")} aria-keyshortcuts="M" style={{ "--i": value === "magnetic" ? 0 : 1, "--n": 2 } as CSSProperties}>
      <button type="button" role="radio" aria-checked={value === "magnetic"} className={value === "magnetic" ? "is-active" : ""} disabled={disabled} onClick={() => onChange("magnetic")} title={tx.t("sketch.placement.magneticTitle")}>
        <Magnet className="sk-mode-icon" size={15} /> {tx.t("sketch.placement.magnetic")}
      </button>
      <button type="button" role="radio" aria-checked={value === "free"} className={value === "free" ? "is-active" : ""} disabled={disabled} onClick={() => onChange("free")} title={tx.t("sketch.placement.freeTitle")}>
        <Move3d className="sk-mode-icon" size={15} /> {tx.t("sketch.placement.free")}
      </button>
      <kbd className="sk-kbd sk-key-hint" aria-hidden>M</kbd>
    </div>
  );
}

/** Portrait | Landscape, with its key (O) on hover. */
export function OrientationSwitch({ value, onChange, disabled }: { value: Orientation; onChange: (o: Orientation) => void; disabled?: boolean }) {
  const tx = useSketchText();
  return (
    <div className="sk-segmented sk-keyed" role="radiogroup" aria-label={tx.t("sketch.orientation.label")} aria-keyshortcuts="O" style={{ "--i": value === "portrait" ? 0 : 1, "--n": 2 } as CSSProperties}>
      {(["portrait", "landscape"] as Orientation[]).map((o) => (
        <button key={o} type="button" role="radio" aria-checked={value === o} className={value === o ? "is-active" : ""} disabled={disabled} onClick={() => onChange(o)} title={tx.t("sketch.orientation.title", { orientation: o })}>
          {o === "portrait" ? <RectangleVertical className="sk-mode-icon" size={15} /> : <RectangleHorizontal className="sk-mode-icon" size={15} />}
          {tx.t(o === "portrait" ? "sketch.orientation.portrait" : "sketch.orientation.landscape")}
        </button>
      ))}
      <kbd className="sk-kbd sk-key-hint" aria-hidden>O</kbd>
    </div>
  );
}

/** The dormer kinds as tiny elevations: gable, hip, shed. */
export function DormerGlyph({ kind }: { kind: DormerKind }) {
  const roof = kind === "gable" ? "M3 9 L9 3 L15 9" : kind === "hip" ? "M3 9 L6 4 H12 L15 9" : "M3 7 L15 4";
  return (
    <svg className="sk-dormer-glyph" width={18} height={14} viewBox="0 0 18 14" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinejoin="round" aria-hidden>
      <path d={roof} />
      <path d={kind === "shed" ? "M4 7 V13 M14 4.3 V13" : "M4 8.5 V13 M14 8.5 V13"} />
    </svg>
  );
}
